import { useState, useEffect, useCallback } from 'react';
import {
    Grid,
    Typography,
    Box,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Paper,
    Tooltip,
    IconButton
} from '@mui/material';
import {
    Radar as RadarIcon,
    Thermostat as ThermometerIcon,
    Air as WindIcon,
    WbSunny as SunIcon,
    Cloud as CloudIcon,
    Grain as SnowIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { DateTime } from 'luxon';
import * as api from '../../api';
import { CollapsibleCard } from '../CollapsibleCard/CollapsibleCard';
import styles from './WeatherDisplay.module.css';

export const WeatherDisplay = () => {
    const [ radarData, setRadarData ] = useState(null);
    const [ forecastData, setForecastData ] = useState([]);
    const [ loading, setLoading ] = useState(true);
    const [ error, setError ] = useState(null);
    const [ lastUpdated, setLastUpdated ] = useState(null);

    const fetchWeatherData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const weatherConfig = {
                latitude: process.env.REACT_APP_RESORT_LAT || 45.34374176839974, // Default to Powder Ridge
                longitude: process.env.REACT_APP_RESORT_LON || -94.305185316461
            };

            // Fetch both radar and forecast data in parallel
            const [ radar, forecast ] = await Promise.all([
                api.getWeatherRadar(weatherConfig.latitude, weatherConfig.longitude),
                api.getWeatherForecast(weatherConfig.latitude, weatherConfig.longitude)
            ]);

            setRadarData(radar);
            setForecastData(forecast);
            setLastUpdated(DateTime.now());
        } catch (err) {
            console.error('Failed to fetch weather data:', err);
            setError('Failed to load weather data from National Weather Service');
        } finally {
            setLoading(false);
        }
    }, []);

    // Load weather data on component mount
    useEffect(() => {
        fetchWeatherData();
    }, [ fetchWeatherData ]);

    const getWeatherIcon = (conditions) => {
        const condition = conditions.toLowerCase();
        if (condition.includes('snow') || condition.includes('blizzard')) {
            return <SnowIcon />;
        } else if (condition.includes('cloud') || condition.includes('overcast')) {
            return <CloudIcon />;
        } else {
            return <SunIcon />;
        }
    };

    const formatTemperature = (temp) => {
        return temp ? `${ temp }°F` : 'N/A';
    };

    if (loading) {
        return (
            <CollapsibleCard title="Weather & Forecast" defaultExpanded={ true }>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={ 200 }>
                    <CircularProgress />
                    <Typography variant="body2" sx={ { ml: 2 } }>
                        Loading weather data...
                    </Typography>
                </Box>
            </CollapsibleCard>
        );
    }

    return (
        <CollapsibleCard
            title="Weather & Forecast"
            defaultExpanded={ true }
            headerIcon={ <SunIcon /> }
            action={
                <Tooltip title="Refresh weather data">
                    <IconButton onClick={ fetchWeatherData } disabled={ loading } size="small">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            }
        >
            { error && (
                <Alert variant='filled' severity="error" sx={ { mb: 2 } }>
                    { error }
                </Alert>
            ) }

            {/* Weather Radar Section */ }
            { radarData && (
                <Grid size={ { xs: 12 } }>
                    <Paper elevation={ 1 } className={ styles.radarSection }>
                        <Box p={ 2 } pb={ 0 }>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={ 2 }>
                                <Typography variant="h6" display="flex" alignItems="center" gap={ 1 }>
                                    <RadarIcon />
                                    Current Weather Radar
                                </Typography>
                                <Tooltip title="Open full radar in new window">
                                    <IconButton
                                        onClick={ () => window.open(radarData.mapUrl, '_blank') }
                                        size="small"
                                    >
                                        <RadarIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                        <Box display="flex" justifyContent="center" mb={ 1 }>
                            <img
                                src={ radarData.radarUrl }
                                alt="Weather Radar"
                                className={ styles.radarImage }
                                onError={ () => setRadarData(null) }
                            />
                        </Box>
                        <Box px={ 2 } pb={ 2 }>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                { radarData.station && (
                                    <Typography variant="caption" color="text.secondary">
                                        Station: { radarData.station }
                                    </Typography>
                                ) }
                                <Typography variant="caption" color="text.secondary">
                                    Source: National Weather Service
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            ) }

            <Grid container spacing={ 3 }>
                {/* Forecast Section */ }
                <Grid size={ { xs: 12 } }>
                    <Paper elevation={ 1 } className={ styles.forecastSection }>
                        <Box p={ 2 }>
                            <Typography variant="h6" display="flex" alignItems="center" gap={ 1 } mb={ 2 }>
                                <SunIcon />
                                5-Day Forecast
                            </Typography>

                            { forecastData.length > 0 ? (
                                <Box className={ styles.forecastContainer }>
                                    { forecastData.map((day, index) => (
                                        <Card
                                            key={ index }
                                            variant="outlined"
                                            className={ styles.forecastCard }
                                        >
                                            <CardContent className={ styles.forecastCardContent }>
                                                <Typography variant="subtitle2" className={ styles.dayName }>
                                                    { day.name }
                                                </Typography>

                                                <Box display="flex" alignItems="center" justifyContent="center" mb={ 1 }>
                                                    { day.icon ? (
                                                        <img
                                                            src={ day.icon }
                                                            alt={ day.conditions }
                                                            className={ styles.weatherIcon }
                                                        />
                                                    ) : (
                                                        getWeatherIcon(day.conditions)
                                                    ) }
                                                </Box>

                                                <Box className={ styles.temperatureContainer }>
                                                    <Typography variant="body2" className={ styles.highTemp }>
                                                        <ThermometerIcon fontSize="small" />
                                                        { formatTemperature(day.highTemp) }
                                                    </Typography>
                                                    { day.lowTemp && (
                                                        <Typography variant="body2" className={ styles.lowTemp }>
                                                            { formatTemperature(day.lowTemp) }
                                                        </Typography>
                                                    ) }
                                                </Box>

                                                <Typography variant="caption" className={ styles.conditions }>
                                                    { day.conditions }
                                                </Typography>

                                                { day.windSpeed && (
                                                    <Box display="flex" alignItems="center" justifyContent="center" mt={ 1 }>
                                                        <WindIcon fontSize="small" />
                                                        <Typography variant="caption" sx={ { ml: 0.5 } }>
                                                            { day.windSpeed } { day.windDirection }
                                                        </Typography>
                                                    </Box>
                                                ) }
                                            </CardContent>
                                        </Card>
                                    )) }
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.primary">
                                    No forecast data available
                                </Typography>
                            ) }
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            { lastUpdated && (
                <Box mt={ 2 } display="flex" justifyContent="center">
                    <Typography variant="caption" color="info">
                        Last updated: { lastUpdated.toFormat('MMM dd, yyyy HH:mm') }
                    </Typography>
                </Box>
            ) }
        </CollapsibleCard>
    );
};